/**
 * Notice Consent Banner
 *
 * Shopify's native cookie banner only renders in consent-required
 * (opt-out-by-default) regions -- EU, Quebec, etc. -- because Shopify ties
 * banner visibility to blocking: showing it anywhere also blocks that region
 * by default (confirmed 2026-07-13; there is no way to decouple the two).
 * So opt-in-by-default regions (e.g. US) get no native banner at all, even
 * though a banner should still greet every first-time visitor there too.
 *
 * This fills that gap with a lightweight NOTICE banner -- Accept only, no
 * Decline, no blocking (nothing needs to be gated; tracking is already
 * allowed by default in these regions). It shows only when Shopify's own
 * API confirms this is an allowed-by-default region, mirroring the same
 * analyticsProcessingAllowed() signal opt-in-cookie-consent.js already uses
 * for Decline-button gating -- no separate country list to maintain.
 *
 * Dismissal = shown once, ever (per browser, until cookies/localStorage are
 * cleared) -- navigating away counts as implicit acceptance, not just an
 * explicit Accept click. This is legally fine specifically because nothing
 * is gated by it: tracking in these regions is already allowed by default
 * regardless of any click, so there's no consent decision being relied on
 * either way (unlike Shopify's native banner in blocking regions, where
 * continued browsing is NOT valid consent under GDPR and an explicit action
 * is required -- that's Shopify's own logic, untouched by this file).
 *
 * Because navigating away counts as acceptance, Shopify's own consent record
 * is set to reflect that at the SAME moment we decide to stop showing the
 * banner (i.e. at display time), not only on an explicit Accept click --
 * otherwise currentVisitorConsent() would stay blank/"undetermined" forever
 * for anyone who never clicks, even though we're treating them as opted in.
 * Still skips that call entirely if the visitor already opted out via
 * GPC/server tag, so an unrelated cookie notice can never silently override
 * a real opt-out with blank-vs-opted-in ambiguity either.
 */
(function () {
  'use strict';

  var DISMISSED_KEY = 'notice_consent_dismissed';
  var WITNESSED_KEY = 'consent_decision_witnessed';
  var CONSENT_REGION_KEY = 'consent_required_region_seen';
  var MAX_ATTEMPTS = 20;
  var CONFIRMATIONS_REQUIRED = 2;

  document.addEventListener('visitorConsentCollected', function () {
    try { localStorage.setItem(WITNESSED_KEY, '1'); } catch (e) {}
  });

  function hasWitnessedDecision() {
    try {
      return localStorage.getItem(WITNESSED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function markConsentRequiredRegion() {
    try { localStorage.setItem(CONSENT_REGION_KEY, '1'); } catch (e) {}
  }

  function isConsentRequiredRegion() {
    try {
      return localStorage.getItem(CONSENT_REGION_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function isDismissed() {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setDismissed() {
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch (e) {
    }
  }

  function nativeBannerPresent() {
    return !!document.querySelector('#shopify-pc__banner, .shopify-pc__banner');
  }

  function hasConsentDecision(cp) {
    try {
      if (typeof cp.currentVisitorConsent !== 'function') return false;
      var c = cp.currentVisitorConsent();
      if (!c || typeof c !== 'object') return false;
      return c.marketing === 'yes' || c.marketing === 'no' ||
             c.analytics === 'yes' || c.analytics === 'no';
    } catch (e) {
      return false;
    }
  }

  function visitorHasOptedOut() {
    try { if (navigator.globalPrivacyControl === true) return true; } catch (e) {}
    if (window.PrivacyOptOut && window.PrivacyOptOut.isOptedOut === true) return true;
    try {
      var stored = localStorage.getItem('privacy_opt_out');
      if (stored && JSON.parse(stored).optedOut === true) return true;
    } catch (e) {}
    return false;
  }

  function recordAcceptance() {
    if (visitorHasOptedOut()) return;

    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.setTrackingConsent !== 'function') return;
    try {
      cp.setTrackingConsent({
        marketing: true,
        analytics: true,
        preferences: true,
        sale_of_data: true
      }, function () {});
    } catch (e) {

    }
  }

  var watchdog = null;

  function watchForLateNativeBanner(banner) {
    if (typeof MutationObserver !== 'function') return;
    watchdog = new MutationObserver(function () {
      if (!nativeBannerPresent()) return;
      banner.style.display = 'none';
      markConsentRequiredRegion(); // native banner really did render here
      try { localStorage.removeItem(DISMISSED_KEY); } catch (e) {}
      console.error('[notice-consent-banner] Shopify\'s native banner appeared ' +
        'after this notice banner was already shown -- retracted. This region ' +
        'was likely misread as allowed-by-default before Shopify\'s API finished ' +
        'resolving the true (consent-required) value.');
      watchdog.disconnect();
    });
    watchdog.observe(document.documentElement, { childList: true, subtree: true });
  }

  function showBanner() {
    var banner = document.getElementById('notice-consent-banner');
    if (!banner) return;
    banner.style.display = 'block';

    setDismissed();
    recordAcceptance();
    watchForLateNativeBanner(banner);

    function onAccept() {
      banner.style.display = 'none';
      if (watchdog) watchdog.disconnect();
      recordAcceptance();
    }

    var acceptBtn = document.getElementById('notice-consent-accept');
    if (acceptBtn) acceptBtn.addEventListener('click', onAccept);

    var closeBtn = document.getElementById('notice-consent-close');
    if (closeBtn) closeBtn.addEventListener('click', onAccept);
  }

  var consecutiveAllowed = 0;

  function evaluate(attempt) {
    if (nativeBannerPresent()) {
      markConsentRequiredRegion();
      return true; // native banner is handling this region
    }
    if (isDismissed() || hasWitnessedDecision() || isConsentRequiredRegion()) return true;

    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return false;

    var region;
    try {
      region = typeof cp.getRegion === 'function' ? cp.getRegion() : null;
    } catch (e) {
      return false;
    }
    if (typeof region !== 'string' || region === '') {
      consecutiveAllowed = 0;
      return false;
    }

    var allowed;
    try {
      allowed = cp.analyticsProcessingAllowed();
    } catch (e) {
      return false;
    }
    if (typeof allowed !== 'boolean') return false;

    if (allowed !== true) {
      consecutiveAllowed = 0;
      return false;
    }

    if (hasConsentDecision(cp)) {
      consecutiveAllowed = 0;
      return true;
    }

    consecutiveAllowed += 1;
    if (consecutiveAllowed < CONFIRMATIONS_REQUIRED) return false;

    showBanner();
    return true;
  }

  if (!evaluate(0)) {
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (evaluate(attempts) || attempts >= MAX_ATTEMPTS) {
        clearInterval(timer);
      }
    }, 500);
  }
})();
